import React, {
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { makeStyles, Theme, createStyles } from "@material-ui/core/styles";
import Modal from "@material-ui/core/Modal";
import Backdrop from "@material-ui/core/Backdrop";
import Button from "@material-ui/core/Button";
import Tooltip from "@material-ui/core/Tooltip";
import {
  GetAllMemberLoginCredentialsDocument,
  GetAllMemberLoginCredentialsQuery,
  GetAllMemberLoginCredentialsStatusEnum,
  LoginInfoEmailTypes,
  useCreateAllMemberLoginCredentialsMutation,
  useGetAllMemberLoginCredentialSuccessfulDownloadTimestampUtcMilliQuery,
  useGetAllMemberMassSendLoginCredentialTimestampUtcMilliQuery,
  useGetSampleLoginInfoEmailQuery,
  useMassSendLoginInfoEmailMutation,
} from "../../../graphql/types";
import { useEnumStrings } from "../../enumStrings";
import { getApolloContext } from "@apollo/client";
import useGetChamberFromUserIdInAuth from "../../common/Hooks/useGetChamberFromUserIdInAuth";
import { getLocalTimestamp } from "../Root/RootAdmin";
import { CircularProgress, IconButton } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { SampleLoginInfoEmailModal } from "../../common/SendLoginInformationModal";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    modal: {
      border: "unset",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    paper: {
      overflow: "scroll",
      maxHeight: "100%",
      borderRadius: "5px",
      minHeight: "500px",
      textAlign: "center",
      width: "740px",
      maxWidth: "100%",
      display: "flex",
      alignItems: "center",
      flexDirection: "column",
      backgroundColor: "#cfd8dc",
      color: "inherit",
      padding: theme.spacing(3),
      paddingTop: theme.spacing(4),
      position: "relative",
    },
    yesButton: {
      minWidth: "200px",
      margin: "10px",
      backgroundColor: "#e1a731",
      color: "#e9e9e9",
      fontWeight: "bold",
      height: "40px",
      textTransform: "none",
    },
    okButton: {
      minWidth: "200px",
      margin: "10px",
      backgroundColor: "#37474f",
      color: "#e9e9e9",
      fontWeight: "bold",
      height: "40px",
      textTransform: "none",
    },
    centeredText: {
      textAlign: "center",
      width: "100%",
    },
    linkText: {
      textDecoration: "underline",
      cursor: "pointer",
      color: "inherit",
    },
    largeTooltip: {
      fontSize: "16px",
      padding: "12px 16px",
    },
  })
);

const DownloadSuccessModal = ({
  open,
  handleClose,
  rawCSVText,
  numOfMembersNotLoggedInYet,
}: {
  open: boolean;
  handleClose: () => void;
  rawCSVText: string;
  numOfMembersNotLoggedInYet: number;
}): ReactElement => {
  const classes = useStyles();
  const enumStrings = useEnumStrings();

  const handleManualDownloadOfCsv = useCallback(() => {
    if (rawCSVText) {
      const blob = new Blob([rawCSVText], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        enumStrings.CSVDownloadNotification.download,
        enumStrings.CSVDownloadNotification.CredentialsCsv
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }, [
    enumStrings.CSVDownloadNotification.CredentialsCsv,
    enumStrings.CSVDownloadNotification.download,
    rawCSVText,
  ]);

  useEffect(() => {
    handleManualDownloadOfCsv();
    // empty dependency array cuz only wanna run this once when the modal opens ... bruh
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Modal className={classes.modal} open={open}>
      <div
        className={classes.paper}
        style={{ justifyContent: "space-between" }}
      >
        <h1 style={{ fontWeight: "bold" }}>
          {enumStrings.CSVDownloadNotification.downloadsuccess}
        </h1>
        <div
          style={{
            display: "flex",
            flex: 1,
            justifyContent: "center",
            alignItems: "center",
            paddingRight: "25px",
            paddingLeft: "25px",
            paddingTop: "25px",
          }}
        >
          <span>
            <h3 style={{ margin: 0, textAlign: "left" }}>
              {enumStrings.CSVDownloadNotification.title}{" "}
              <span style={{ fontWeight: "bold" }}>
                {numOfMembersNotLoggedInYet}
              </span>{" "}
              {enumStrings.CSVDownloadNotification.numOfMembersNotLoggedInYet}{" "}
              <span style={{ fontWeight: "bold" }}>
                {enumStrings.CSVDownloadNotification.notLoggedInYet}
              </span>{" "}
              {enumStrings.CSVDownloadNotification.activeMembershipStatus}
              <br />
              <br />
              {enumStrings.CSVDownloadNotification.downloadInfo}
            </h3>
          </span>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          <Button
            className={classes.yesButton}
            onClick={handleManualDownloadOfCsv}
            variant="contained"
          >
            {enumStrings.CSVDownloadNotification.download}
          </Button>
          <Button
            className={classes.okButton}
            onClick={handleClose}
            variant="contained"
          >
            {enumStrings.CSVDownloadNotification.close}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

const DownloadPendingModal = ({
  open,
  setAllMemberLoginCredentialsRawCSVText,
  setDownloadFailureModalOpen,
  refetchDownloadTimestamp,
}: {
  open: boolean;
  setAllMemberLoginCredentialsRawCSVText: (value: string) => void;
  setDownloadFailureModalOpen: (value: boolean) => void;
  refetchDownloadTimestamp: () => void;
}) => {
  const classes = useStyles();

  const enumStrings = useEnumStrings();

  const modalOpenTime = useMemo(() => {
    return new Date();
  }, []);

  const apolloClient = useContext(getApolloContext());

  const handleAllMembereLoginCredentialsDownload = useCallback(
    async ({ downloadProcessId }: { downloadProcessId: string }) => {
      // if 15 minutes have passed since the modal opened, close the modal
      if (new Date().getTime() - modalOpenTime.getTime() > 15 * 60 * 1000) {
        setDownloadFailureModalOpen(true);
        return;
      }

      if (downloadProcessId && downloadProcessId.length > 0) {
        const allMemberLoginCredentialsData = (await apolloClient.client?.query(
          {
            query: GetAllMemberLoginCredentialsDocument,
            variables: {
              processId: downloadProcessId,
            },
            fetchPolicy: "network-only",
          }
        )) as {
          data: GetAllMemberLoginCredentialsQuery | undefined;
        };

        if (
          allMemberLoginCredentialsData?.data?.getAllMemberLoginCredentials
            ?.status
        ) {
          const downloadStatus =
            allMemberLoginCredentialsData?.data?.getAllMemberLoginCredentials
              ?.status;

          if (
            downloadStatus === GetAllMemberLoginCredentialsStatusEnum.Processing
          ) {
            // pause for 5 seconds before trying again
            await new Promise((resolve) => setTimeout(resolve, 5000));

            await handleAllMembereLoginCredentialsDownload({
              downloadProcessId,
            });
            return;
          }
          if (
            downloadStatus === GetAllMemberLoginCredentialsStatusEnum.Success
          ) {
            if (
              allMemberLoginCredentialsData?.data?.getAllMemberLoginCredentials
                ?.successCSVRawTextOfAllMemberLoginCredentials
            ) {
              setAllMemberLoginCredentialsRawCSVText(
                allMemberLoginCredentialsData?.data
                  ?.getAllMemberLoginCredentials
                  ?.successCSVRawTextOfAllMemberLoginCredentials
              );
              // Refetch the timestamp to update the UI
              refetchDownloadTimestamp();
            } else {
              setDownloadFailureModalOpen(true);
            }
            return;
          }
          if (
            downloadStatus === GetAllMemberLoginCredentialsStatusEnum.Failure
          ) {
            setDownloadFailureModalOpen(true);
            return;
          }
        }
      }
    },
    [
      apolloClient.client,
      modalOpenTime,
      setAllMemberLoginCredentialsRawCSVText,
      setDownloadFailureModalOpen,
      refetchDownloadTimestamp,
    ]
  );

  const [createAllMemberLoginCredentialsMutation] =
    useCreateAllMemberLoginCredentialsMutation();

  useEffect(() => {
    const executeDownloadAllMemberLoginCredentials = async () => {
      const createRes = await createAllMemberLoginCredentialsMutation();
      if (createRes.data?.createAllMemberLoginCredentials?.processId) {
        handleAllMembereLoginCredentialsDownload({
          downloadProcessId:
            createRes.data.createAllMemberLoginCredentials.processId,
        });
      }
    };
    executeDownloadAllMemberLoginCredentials();
    // empty dependency array cuz only wanna run this once when the modal opens ... bruh
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Modal className={classes.modal} open={open}>
      <div
        className={classes.paper}
        style={{ justifyContent: "space-between" }}
      >
        <h1 style={{ fontWeight: "bold" }}>
          {" "}
          {enumStrings.DownloadMemberCredentials.downloadInProgress}
        </h1>
        <div
          style={{
            display: "flex",
            flex: 1,
            justifyContent: "center",
            alignItems: "center",
            paddingRight: "25px",
            paddingLeft: "25px",
            paddingTop: "25px",
          }}
        >
          <span>
            <h3 style={{ margin: 0 }}>
              {enumStrings.DownloadMemberCredentials.description}
              <br />
              <br />

              {enumStrings.DownloadMemberCredentials.downloadIn5}
            </h3>
          </span>
        </div>
        <div
          style={{
            display: "flex",
            flex: 1,
            justifyContent: "center",
          }}
        >
          <CircularProgress
            style={{
              color: "#E1A731",
            }}
          />
        </div>
      </div>
    </Modal>
  );
};

const EmailPendingModal = ({
  open,
  setEmailSuccessModalOpen,
  setEmailFailureModalOpen,
}: {
  open: boolean;
  setEmailSuccessModalOpen: (value: boolean) => void;
  setEmailFailureModalOpen: (value: boolean) => void;
}) => {
  const classes = useStyles();
  const enumStrings = useEnumStrings();
  const [massSendLoginInfoEmail] = useMassSendLoginInfoEmailMutation();

  useEffect(() => {
    const executeMassEmailAllMemberLoginCredentials = async () => {
      try {
        // Get all chamber members who haven't logged in yet
        const { data } = await massSendLoginInfoEmail({
          refetchQueries: [
            "getChamberMembers",
            "getChamber",
            "getAllMemberMassSendLoginCredentialTimestampUtcMilli",
          ],
        });

        if (data?.massSendLoginInfoEmail) {
          setEmailSuccessModalOpen(true);
        } else {
          setEmailFailureModalOpen(true);
        }
      } catch (error) {
        console.error("Error sending mass emails:", error);
        setEmailFailureModalOpen(true);
      }
    };

    executeMassEmailAllMemberLoginCredentials();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Modal className={classes.modal} open={open}>
      <div
        className={classes.paper}
        style={{ justifyContent: "space-between" }}
      >
        <h1 style={{ fontWeight: "bold" }}>
          {enumStrings.DownloadMemberCredentials.emailPending.title}
        </h1>
        <div
          style={{
            display: "flex",
            flex: 1,
            justifyContent: "center",
            alignItems: "center",
            paddingRight: "25px",
            paddingLeft: "25px",
            paddingTop: "25px",
          }}
        >
          <span>
            <h3 style={{ margin: 0, textAlign: "left" }}>
              {enumStrings.DownloadMemberCredentials.emailPending.description}
              <br />
              <br />
              {enumStrings.DownloadMemberCredentials.description}
            </h3>
          </span>
        </div>
        <div
          style={{
            display: "flex",
            flex: 1,
            justifyContent: "center",
          }}
        >
          <CircularProgress
            style={{
              color: "#E1A731",
            }}
          />
        </div>
      </div>
    </Modal>
  );
};

const EmailSuccessModal = ({
  open,
  handleClose,
}: {
  open: boolean;
  handleClose: () => void;
}): ReactElement => {
  const classes = useStyles();
  const enumStrings = useEnumStrings();

  return (
    <Modal className={classes.modal} open={open}>
      <div
        className={classes.paper}
        style={{ justifyContent: "space-between" }}
      >
        <h1 style={{ fontWeight: "bold" }}>
          {enumStrings.DownloadMemberCredentials.emailSuccess.title}
        </h1>
        <div
          style={{
            display: "flex",
            flex: 1,
            justifyContent: "center",
            alignItems: "center",
            paddingRight: "25px",
            paddingLeft: "25px",
            paddingTop: "25px",
          }}
        >
          <span>
            <h3 style={{ margin: 0, textAlign: "left" }}>
              {enumStrings.DownloadMemberCredentials.emailSuccess.description}
              <br />
              <br />
              {
                enumStrings.DownloadMemberCredentials.emailSuccess
                  .additionalInfo
              }
            </h3>
          </span>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          <Button
            className={classes.okButton}
            onClick={handleClose}
            variant="contained"
          >
            {enumStrings.CSVDownloadNotification.close}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

const EmailConfirmationModal = ({
  open,
  handleClose,
  handleConfirm,
  numOfMembersNotLoggedInYet,
  lastEmailSentTimestamp,
}: {
  open: boolean;
  handleClose: () => void;
  handleConfirm: () => void;
  numOfMembersNotLoggedInYet: number;
  lastEmailSentTimestamp?: number;
}): ReactElement => {
  const classes = useStyles();
  const enumStrings = useEnumStrings();

  const timeAgoDisplay = getTimeAgoText({
    timestamp: lastEmailSentTimestamp,
    enumStrings,
    lastActionPrefix:
      enumStrings.DownloadMemberCredentials.emailConfirmation.lastSentPrefix,
    lastActionSuffix:
      enumStrings.DownloadMemberCredentials.emailConfirmation.lastSentSuffix,
    neverActionText:
      enumStrings.DownloadMemberCredentials.emailConfirmation.neverSent,
  });

  return (
    <Modal className={classes.modal} open={open} onClose={handleClose}>
      <div
        className={classes.paper}
        style={{ justifyContent: "space-between" }}
      >
        <h1 style={{ fontWeight: "bold" }}>
          {enumStrings.DownloadMemberCredentials.emailConfirmation.title}
        </h1>
        <div
          style={{
            display: "flex",
            flex: 1,
            justifyContent: "center",
            alignItems: "center",
            paddingRight: "25px",
            paddingLeft: "25px",
            paddingTop: "0px",
          }}
        >
          <span>
            {/* @ts-ignore */}
            <h3 style={{ margin: 0, textAlign: "center", textWrap: "balance" }}>
              {
                enumStrings.DownloadMemberCredentials.emailConfirmation
                  .sendToMembersPrefix
              }{" "}
              <span style={{ fontWeight: "bold", color: "#37474f" }}>
                {numOfMembersNotLoggedInYet}
              </span>{" "}
              {numOfMembersNotLoggedInYet !== 1
                ? enumStrings.DownloadMemberCredentials.emailConfirmation
                    .members
                : enumStrings.DownloadMemberCredentials.emailConfirmation
                    .member}{" "}
              {
                enumStrings.DownloadMemberCredentials.emailConfirmation
                  .sendToMembersSuffix
              }
              <br />
              <br />
              <span
                style={{
                  fontWeight: "bold",
                  color: "#37474f",
                  fontSize: "18px",
                }}
              >
                {timeAgoDisplay}
              </span>
              <br />
              <br />
              {enumStrings.DownloadMemberCredentials.emailConfirmation.message}
            </h3>
          </span>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          <Button
            className={classes.yesButton}
            onClick={handleConfirm}
            variant="contained"
          >
            {
              enumStrings.DownloadMemberCredentials.emailConfirmation
                .confirmButton
            }
          </Button>
          <Button
            className={classes.okButton}
            onClick={handleClose}
            variant="contained"
          >
            {
              enumStrings.DownloadMemberCredentials.emailConfirmation
                .cancelButton
            }
          </Button>
        </div>
      </div>
    </Modal>
  );
};

const DownloadConfirmationModal = ({
  open,
  handleClose,
  handleConfirm,
  numOfMembersNotLoggedInYet,
  lastDownloadTimestamp,
}: {
  open: boolean;
  handleClose: () => void;
  handleConfirm: () => void;
  numOfMembersNotLoggedInYet: number;
  lastDownloadTimestamp?: number;
}): ReactElement => {
  const classes = useStyles();
  const enumStrings = useEnumStrings();

  const timeAgoDisplay = getTimeAgoText({
    timestamp: lastDownloadTimestamp,
    enumStrings,
    lastActionPrefix:
      enumStrings.DownloadMemberCredentials.downloadConfirmation.lastSentPrefix,
    lastActionSuffix:
      enumStrings.DownloadMemberCredentials.downloadConfirmation.lastSentSuffix,
    neverActionText:
      enumStrings.DownloadMemberCredentials.downloadConfirmation.neverSent,
  });

  return (
    <Modal className={classes.modal} open={open} onClose={handleClose}>
      <div
        className={classes.paper}
        style={{ justifyContent: "space-between" }}
      >
        <h1 style={{ fontWeight: "bold" }}>
          {enumStrings.DownloadMemberCredentials.downloadConfirmation.title}
        </h1>
        <div
          style={{
            display: "flex",
            flex: 1,
            justifyContent: "center",
            alignItems: "center",
            paddingRight: "25px",
            paddingLeft: "25px",
            paddingTop: "0px",
          }}
        >
          <span>
            {/* @ts-ignore */}
            <h3 style={{ margin: 0, textAlign: "center", textWrap: "balance" }}>
              {
                enumStrings.DownloadMemberCredentials.downloadConfirmation
                  .downloadForMembersPrefix
              }{" "}
              <span style={{ fontWeight: "bold", color: "#37474f" }}>
                {numOfMembersNotLoggedInYet}
              </span>{" "}
              {numOfMembersNotLoggedInYet !== 1
                ? enumStrings.DownloadMemberCredentials.downloadConfirmation
                    .members
                : enumStrings.DownloadMemberCredentials.downloadConfirmation
                    .member}{" "}
              {
                enumStrings.DownloadMemberCredentials.downloadConfirmation
                  .downloadForMembersSuffix
              }
              <br />
              <br />
              <span
                style={{
                  fontWeight: "bold",
                  color: "#37474f",
                  fontSize: "18px",
                }}
              >
                {timeAgoDisplay}
              </span>
              <br />
              <br />
              {
                enumStrings.DownloadMemberCredentials.downloadConfirmation
                  .message
              }
            </h3>
          </span>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          <Button
            className={classes.yesButton}
            onClick={handleConfirm}
            variant="contained"
          >
            {
              enumStrings.DownloadMemberCredentials.downloadConfirmation
                .confirmButton
            }
          </Button>
          <Button
            className={classes.okButton}
            onClick={handleClose}
            variant="contained"
          >
            {
              enumStrings.DownloadMemberCredentials.downloadConfirmation
                .cancelButton
            }
          </Button>
        </div>
      </div>
    </Modal>
  );
};

const DownloadFailureModal = ({
  open,
  handleClose,
}: {
  open: boolean;
  handleClose: () => void;
}) => {
  const classes = useStyles();
  const enumStrings = useEnumStrings();

  return (
    <Modal className={classes.modal} open={open}>
      <div
        className={classes.paper}
        style={{ justifyContent: "space-between" }}
      >
        <h1 style={{ fontWeight: "bold" }}>
          {enumStrings.downloadFailureModal.title}
        </h1>
        <div
          style={{
            display: "flex",
            flex: 1,
            justifyContent: "center",
            alignItems: "center",
            paddingRight: "25px",
            paddingLeft: "25px",
            paddingTop: "25px",
          }}
        >
          <span>
            <h3 style={{ margin: 0 }}>
              {enumStrings.downloadFailureModal.message}
              <br />
              {enumStrings.downloadFailureModal.pleaseContact}{" "}
              {enumStrings.supportContactEmail}{" "}
              {enumStrings.downloadFailureModal.thisIsTheError}
            </h3>
          </span>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          <Button
            className={classes.okButton}
            onClick={handleClose}
            variant="contained"
          >
            {enumStrings.downloadFailureModal.closeButtonText}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// Shared utility function to calculate time ago text
const getTimeAgoText = ({
  timestamp,
  enumStrings,
  lastActionPrefix,
  lastActionSuffix,
  neverActionText,
}: {
  timestamp?: number;
  enumStrings: ReturnType<typeof useEnumStrings>;
  lastActionPrefix: string;
  lastActionSuffix: string;
  neverActionText: string;
}) => {
  if (!timestamp) {
    return neverActionText;
  }

  const now = Date.now();
  const diffInMs = now - timestamp;
  const diffInMinutes = Math.floor(diffInMs / 60000);
  const diffInHours = Math.floor(diffInMinutes / 60);
  const diffInDays = Math.floor(diffInHours / 24);

  let timeAgoText = "";
  if (diffInDays > 0) {
    timeAgoText = `${diffInDays} ${
      diffInDays !== 1
        ? enumStrings.DownloadMemberCredentials.timeAgo.days
        : enumStrings.DownloadMemberCredentials.timeAgo.day
    }`;
  } else if (diffInHours > 0) {
    timeAgoText = `${diffInHours} ${
      diffInHours !== 1
        ? enumStrings.DownloadMemberCredentials.timeAgo.hours
        : enumStrings.DownloadMemberCredentials.timeAgo.hour
    }`;
  } else if (diffInMinutes > 0) {
    timeAgoText = `${diffInMinutes} ${
      diffInMinutes !== 1
        ? enumStrings.DownloadMemberCredentials.timeAgo.minutes
        : enumStrings.DownloadMemberCredentials.timeAgo.minute
    }`;
  } else {
    timeAgoText =
      enumStrings.DownloadMemberCredentials.timeAgo.lessThanOneMinute;
  }

  return `${lastActionPrefix} ${timeAgoText} ${lastActionSuffix}`;
};

const DownloadAllMemberLoginCredentialsModal = ({
  open,
  handleClose,
  numOfMembersNotLoggedInYet,
}: {
  open: boolean;
  handleClose: () => void;
  numOfMembersNotLoggedInYet: number;
}): ReactElement => {
  const chamberInfo = useGetChamberFromUserIdInAuth();

  const classes = useStyles();
  const enumStrings = useEnumStrings();

  const [downloadSuccessModalOpen, setDownloadSuccessModalOpen] =
    useState(false);

  const [downloadPendingModalOpen, setDownloadPendingModalOpen] =
    useState(false);

  const [downloadFailureModalOpen, setDownloadFailureModalOpen] =
    useState(false);

  const [emailConfirmationModalOpen, setEmailConfirmationModalOpen] =
    useState(false);
  const [emailPendingModalOpen, setEmailPendingModalOpen] = useState(false);

  const [emailSuccessModalOpen, setEmailSuccessModalOpen] = useState(false);

  const [emailFailureModalOpen, setEmailFailureModalOpen] = useState(false);

  const [downloadConfirmationModalOpen, setDownloadConfirmationModalOpen] =
    useState(false);

  const [sampleLoginInfoEmailModalOpen, setSampleLoginInfoEmailModalOpen] =
    useState(false);

  const closeSampleLoginInfoEmailModal = useCallback(() => {
    setSampleLoginInfoEmailModalOpen(false);
  }, []);

  const [rawCSVText, setRawCSVText] = useState("");

  const setAllMemberLoginCredentialsRawCSVText = useCallback(
    (value: string) => {
      setRawCSVText(value);
      setDownloadSuccessModalOpen(true);
    },
    [setRawCSVText, setDownloadSuccessModalOpen]
  );

  const {
    data: allMemberLoginCredentialSuccessfulDownloadTimestampUtcMilliData,
    refetch: refetchDownloadTimestamp,
  } = useGetAllMemberLoginCredentialSuccessfulDownloadTimestampUtcMilliQuery({
    variables: {
      chamberId: chamberInfo?.id || "",
    },
    skip: !chamberInfo?.id,
  });

  const {
    data: allMemberMassSendLoginCredentialTimestampUtcMilliData,
    loading: loadingLastEmailTimestamp,
  } = useGetAllMemberMassSendLoginCredentialTimestampUtcMilliQuery({
    variables: {
      chamberId: chamberInfo?.id || "",
    },
    skip: !chamberInfo?.id,
  });

  // Sample email preview functionality
  const { data: getSampleLoginEmail } = useGetSampleLoginInfoEmailQuery({
    variables: {
      type: LoginInfoEmailTypes.BrandNew,
      chamberMemberId: "", // For mass email preview, we don't need a specific member ID
    },
  });

  // Calculate if email button should be disabled (1 hour cooldown)
  const lastEmailSentTimestamp =
    allMemberMassSendLoginCredentialTimestampUtcMilliData?.getChamber
      ?.allMemberMassSendLoginCredentialTimestampUtcMilli;

  const { isEmailButtonDisabled, minutesRemaining: emailMinutesRemaining } =
    useMemo(() => {
      if (!lastEmailSentTimestamp) {
        return { isEmailButtonDisabled: false, minutesRemaining: 0 };
      }

      const now = Date.now();
      const oneHourInMs = 60 * 60 * 1000;
      const timeSinceLastSend = now - lastEmailSentTimestamp;

      if (timeSinceLastSend < oneHourInMs) {
        const remaining = Math.ceil((oneHourInMs - timeSinceLastSend) / 60000);
        return { isEmailButtonDisabled: true, minutesRemaining: remaining };
      }

      return { isEmailButtonDisabled: false, minutesRemaining: 0 };
    }, [lastEmailSentTimestamp]);

  // Calculate if download button should be disabled (1 hour cooldown)
  const lastDownloadTimestamp =
    allMemberLoginCredentialSuccessfulDownloadTimestampUtcMilliData?.getChamber
      ?.allMemberLoginCredentialSuccessfulDownloadTimestampUtcMilli;

  const {
    isDownloadButtonDisabled,
    minutesRemaining: downloadMinutesRemaining,
  } = useMemo(() => {
    if (!lastDownloadTimestamp) {
      return { isDownloadButtonDisabled: false, minutesRemaining: 0 };
    }

    const now = Date.now();
    const oneHourInMs = 60 * 60 * 1000;
    const timeSinceLastDownload = now - lastDownloadTimestamp;

    if (timeSinceLastDownload < oneHourInMs) {
      const remaining = Math.ceil(
        (oneHourInMs - timeSinceLastDownload) / 60000
      );
      return { isDownloadButtonDisabled: true, minutesRemaining: remaining };
    }

    return { isDownloadButtonDisabled: false, minutesRemaining: 0 };
  }, [lastDownloadTimestamp]);

  return (
    <>
      {downloadPendingModalOpen ? (
        <DownloadPendingModal
          open={downloadPendingModalOpen}
          setAllMemberLoginCredentialsRawCSVText={
            setAllMemberLoginCredentialsRawCSVText
          }
          setDownloadFailureModalOpen={setDownloadFailureModalOpen}
          refetchDownloadTimestamp={refetchDownloadTimestamp}
        />
      ) : null}
      {downloadSuccessModalOpen && rawCSVText && rawCSVText.length > 0 ? (
        <DownloadSuccessModal
          open={downloadSuccessModalOpen}
          handleClose={handleClose}
          rawCSVText={rawCSVText}
          numOfMembersNotLoggedInYet={numOfMembersNotLoggedInYet}
        />
      ) : null}
      {emailConfirmationModalOpen && (
        <EmailConfirmationModal
          open={emailConfirmationModalOpen}
          handleClose={() => setEmailConfirmationModalOpen(false)}
          handleConfirm={() => {
            setEmailConfirmationModalOpen(false);
            setEmailPendingModalOpen(true);
          }}
          numOfMembersNotLoggedInYet={numOfMembersNotLoggedInYet}
          lastEmailSentTimestamp={lastEmailSentTimestamp ?? undefined}
        />
      )}
      {downloadConfirmationModalOpen && (
        <DownloadConfirmationModal
          open={downloadConfirmationModalOpen}
          handleClose={() => setDownloadConfirmationModalOpen(false)}
          handleConfirm={() => {
            setDownloadConfirmationModalOpen(false);
            setDownloadPendingModalOpen(true);
          }}
          numOfMembersNotLoggedInYet={numOfMembersNotLoggedInYet}
          lastDownloadTimestamp={lastDownloadTimestamp ?? undefined}
        />
      )}
      {emailPendingModalOpen ? (
        <EmailPendingModal
          open={emailPendingModalOpen}
          setEmailSuccessModalOpen={setEmailSuccessModalOpen}
          setEmailFailureModalOpen={setEmailFailureModalOpen}
        />
      ) : null}
      {emailSuccessModalOpen ? (
        <EmailSuccessModal
          open={emailSuccessModalOpen}
          handleClose={handleClose}
        />
      ) : null}
      <DownloadFailureModal
        open={downloadFailureModalOpen || emailFailureModalOpen}
        handleClose={handleClose}
      />
      <SampleLoginInfoEmailModal
        open={sampleLoginInfoEmailModalOpen}
        handleClose={closeSampleLoginInfoEmailModal}
        emailHtml={getSampleLoginEmail?.getSampleLoginInfoEmail?.emailHtml}
        modalType={"memberTable"}
      />
      <Modal
        className={classes.modal}
        style={{
          ...(downloadFailureModalOpen ||
          downloadSuccessModalOpen ||
          downloadPendingModalOpen ||
          downloadConfirmationModalOpen ||
          emailConfirmationModalOpen ||
          emailPendingModalOpen ||
          emailSuccessModalOpen ||
          emailFailureModalOpen ||
          sampleLoginInfoEmailModalOpen
            ? { display: "none" }
            : {}),
        }}
        open={open}
        onClose={handleClose}
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <div className={classes.paper}>
          <IconButton
            style={{
              position: "absolute",
              right: "10px",
              top: "10px",
              color: "#37474f",
              fontWeight: "bold",
              borderRadius: "100px",
              padding: 5,
            }}
            onClick={handleClose}
          >
            <CloseIcon />
          </IconButton>
          <h1 style={{ fontWeight: "bold" }}>
            {enumStrings.DownloadMemberCredentials.mainModal.title}
          </h1>

          {/* Primary content */}
          <div style={{ width: "100%", padding: "20px", paddingTop: "30px" }}>
            {/* Mass Email section */}
            <div style={{ textAlign: "left" }}>
              <p
                style={{
                  fontWeight: "bold",
                  marginBottom: "10px",
                  fontSize: "19px",
                  textAlign: "center",
                  width: "100%",
                }}
              >
                {enumStrings.sendLoginModal.option1Header}
              </p>
              <h3 style={{ margin: 0 }}>
                {
                  enumStrings.DownloadMemberCredentials.mainModal
                    .emailInstructions
                }
              </h3>
            </div>

            {/* Last Email Sent Timestamp */}
            <h3>
              {allMemberMassSendLoginCredentialTimestampUtcMilliData?.getChamber
                ?.allMemberMassSendLoginCredentialTimestampUtcMilli ? (
                <p style={{ marginTop: "20px" }}>
                  {
                    enumStrings.DownloadMemberCredentials.mainModal
                      .lastEmailSent
                  }{" "}
                  {getLocalTimestamp(
                    allMemberMassSendLoginCredentialTimestampUtcMilliData
                      .getChamber
                      .allMemberMassSendLoginCredentialTimestampUtcMilli,
                    true
                  )}
                </p>
              ) : (
                <p />
              )}
            </h3>

            {/* Email Button */}
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexDirection: "column",
              }}
            >
              <Tooltip
                title={
                  loadingLastEmailTimestamp
                    ? "Loading..."
                    : isEmailButtonDisabled
                    ? `${
                        enumStrings.DownloadMemberCredentials
                          .disabledButtonTooltip.waitPrefix
                      } ${emailMinutesRemaining} ${
                        emailMinutesRemaining !== 1
                          ? enumStrings.DownloadMemberCredentials
                              .disabledButtonTooltip.minutes
                          : enumStrings.DownloadMemberCredentials
                              .disabledButtonTooltip.minute
                      } ${
                        enumStrings.DownloadMemberCredentials
                          .disabledButtonTooltip.waitSuffix
                      }`
                    : ""
                }
                arrow
                placement="top"
                classes={{
                  tooltip: classes.largeTooltip,
                }}
              >
                <span>
                  <Button
                    className={classes.yesButton}
                    onClick={() => {
                      setEmailConfirmationModalOpen(true);
                    }}
                    variant="contained"
                    disabled={
                      loadingLastEmailTimestamp || isEmailButtonDisabled
                    }
                    style={{
                      ...((loadingLastEmailTimestamp ||
                        isEmailButtonDisabled) && {
                        backgroundColor: "#9e9e9e",
                        cursor: "not-allowed",
                      }),
                    }}
                  >
                    {
                      enumStrings.DownloadMemberCredentials.mainModal
                        .emailButton
                    }
                  </Button>
                </span>
              </Tooltip>
              <span
                onClick={() => {
                  setSampleLoginInfoEmailModalOpen(true);
                }}
                className={classes.linkText}
              >
                {enumStrings.DownloadMemberCredentials.mainModal.previewLink}
              </span>
            </div>

            {/* Download Instructions */}
            <div
              style={{
                textAlign: "left",
                marginTop: "60px",
                marginBottom: "20px",
              }}
            >
              <p
                style={{
                  fontWeight: "bold",
                  marginBottom: "10px",
                  fontSize: "19px",
                  textAlign: "center",
                  width: "100%",
                }}
              >
                {enumStrings.sendLoginModal.option2Header}
              </p>
              <h3 style={{ margin: 0 }}>
                {
                  enumStrings.DownloadMemberCredentials.mainModal
                    .downloadInstructions
                }
              </h3>
            </div>

            {/* Last Download Timestamp */}
            <h3>
              {allMemberLoginCredentialSuccessfulDownloadTimestampUtcMilliData
                ?.getChamber
                ?.allMemberLoginCredentialSuccessfulDownloadTimestampUtcMilli ? (
                <p>
                  {
                    enumStrings.DownloadMemberCredentials.mainModal
                      .lastDownloaded
                  }{" "}
                  {getLocalTimestamp(
                    allMemberLoginCredentialSuccessfulDownloadTimestampUtcMilliData
                      .getChamber
                      .allMemberLoginCredentialSuccessfulDownloadTimestampUtcMilli,
                    true
                  )}
                </p>
              ) : (
                <p />
              )}
            </h3>

            {/* Download Button */}
            <div style={{ display: "flex", justifyContent: "center" }}>
              <Tooltip
                title={
                  isDownloadButtonDisabled
                    ? `${
                        enumStrings.DownloadMemberCredentials
                          .disabledButtonTooltip.waitPrefix
                      } ${downloadMinutesRemaining} ${
                        downloadMinutesRemaining !== 1
                          ? enumStrings.DownloadMemberCredentials
                              .disabledButtonTooltip.minutes
                          : enumStrings.DownloadMemberCredentials
                              .disabledButtonTooltip.minute
                      } ${
                        enumStrings.DownloadMemberCredentials
                          .disabledButtonTooltip.waitSuffix
                      }`
                    : ""
                }
                arrow
                placement="top"
                classes={{
                  tooltip: classes.largeTooltip,
                }}
              >
                <span>
                  <Button
                    className={classes.okButton}
                    onClick={() => {
                      setDownloadConfirmationModalOpen(true);
                    }}
                    variant="contained"
                    disabled={isDownloadButtonDisabled}
                    style={{
                      ...(isDownloadButtonDisabled && {
                        backgroundColor: "#9e9e9e",
                        cursor: "not-allowed",
                      }),
                    }}
                  >
                    {
                      enumStrings.DownloadMemberCredentials.mainModal
                        .downloadButton
                    }
                  </Button>
                </span>
              </Tooltip>
            </div>
            <br />
            <br />
            <div
              style={{
                paddingLeft: "8px",
                paddingRight: "8px",
                textAlign: "start",
              }}
            >
              <span style={{ fontWeight: "bold" }}>
                {enumStrings.DownloadMemberCredentials.noteTitle}
              </span>{" "}
              {enumStrings.DownloadMemberCredentials.temporaryPasswordNote}
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default DownloadAllMemberLoginCredentialsModal;
